import { Component, HostListener, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, QueryList, ViewChildren } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ChartConfiguration, ChartData, ChartDataset } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { HourlyDataPoint } from '../version.service';
import { dragZoom, uiLocale } from '../shared/chart.utils';

interface DatasetDef {
  field:  keyof HourlyDataPoint;
  label:  string;   // i18n key (the grid's column names)
  color:  string;   // solid color used for border (line) and background (bar)
}

const DATASETS: DatasetDef[] = [
  { field: 'generationKwh',  label: 'COLS.ERTRAG',      color: '#f59e0b' },
  { field: 'consumptionKwh', label: 'COLS.VERBRAUCH',   color: '#0d6efd' },
  { field: 'gridImportKwh',  label: 'COLS.BEZUG',       color: '#ef4444' },
  { field: 'gridExportKwh',  label: 'COLS.EINSPEISUNG', color: '#22c55e' },
];

/** Phones (< 576 px): fewer, smaller ticks, no axis titles, a compact legend and tooltip. */
const PHONE_MAX_W = 576;

/** Time-axis tick steps (minutes): the first that gives at most ~12 ticks across the visible span. */
const TICK_STEPS_MIN = [5, 15, 30, 60, 120, 180, 360];

/** Europe/Berlin's offset from UTC at `ts` (ms; whole hours). */
function berlinOffsetMs(ts: number): number {
  const h = Number(new Date(ts).toLocaleString('en-GB', { timeZone: 'Europe/Berlin', hour: '2-digit', hourCycle: 'h23' }));
  return isNaN(h) ? 0 : ((h - new Date(ts).getUTCHours() + 24) % 24) * 3_600_000;
}

/** Ticks at whole 5 min / quarter / half / full hours of Berlin time within [min, max] (at most ~maxTicks). */
function berlinTimeTicks(min: number, max: number, maxTicks = 12): { value: number }[] {
  if (!isFinite(min) || !isFinite(max) || max <= min) return [];
  const stepMin = TICK_STEPS_MIN.find(m => (max - min) / (m * 60_000) <= maxTicks) ?? TICK_STEPS_MIN[TICK_STEPS_MIN.length - 1];
  const step = stepMin * 60_000;
  const off = berlinOffsetMs(min);
  const ticks: { value: number }[] = [];
  for (let t = Math.ceil((min + off) / step) * step - off; t <= max; t += step) ticks.push({ value: t });
  return ticks;
}

@Component({
  selector: 'app-hourly-chart',
  template: `
    <div class="hourly-box">
      <canvas *ngIf="!field" baseChart [data]="lineData"  [options]="lineOptions"  type="line"></canvas>
      <canvas *ngIf="field"  baseChart [data]="barData"   [options]="barOptions"   type="bar"></canvas>
      <!-- bottom right: the legend (top) wraps to two lines on a phone -->
      <button *ngIf="zoomed" type="button" class="btn btn-sm btn-outline-secondary dash-chart-reset"
              style="top:auto;bottom:0;"
              (click)="resetZoom()">{{ 'CHART_RESET_ZOOM' | translate }}</button>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .hourly-box { position: relative; height: clamp(240px, 50vh, 560px); }
    /* in a phone sheet's fill body (class dash-sheet-fill-item on the host): the chart takes the free height */
    @media (max-width: 575.98px) {
      :host(.dash-sheet-fill-item) .hourly-box { height: 100%; }
    }
  `],
})
export class HourlyChartComponent implements OnChanges, OnDestroy {
  @ViewChildren(BaseChartDirective) chartDirectives!: QueryList<BaseChartDirective>;
  @Input() data:    HourlyDataPoint[] = [];
  @Input() field:   string | null     = null;
  @Input() darkMode = false;
  /** Deep-link id of the chart — the overlay's view path ('statistik/2026-10-18'): the legend selection
   *  and the zoom window (a time window: the x axis is linear time) go into the link. null = not linked. */
  @Input() linkId:  string | null     = null;

  lineData:    ChartData<'line'> = { datasets: [] };
  lineOptions: ChartConfiguration<'line'>['options'] = {};
  barData:     ChartData<'bar', { x: number; y: number }[]> = { datasets: [] };
  barOptions:  ChartConfiguration<'bar'>['options']  = {};
  zoomed = false;
  private phone = window.innerWidth < PHONE_MAX_W;
  private readonly langSub: Subscription;

  constructor(private readonly zone: NgZone, private readonly translate: TranslateService) {
    // labels, axis titles and times follow a language switch
    this.langSub = translate.onLangChange.subscribe(() => this.build());
  }

  ngOnDestroy(): void { this.langSub.unsubscribe(); }

  /** Crossing 576 px: rebuild the options (phone ticks / legend). */
  @HostListener('window:resize')
  onResize(): void {
    const phone = window.innerWidth < PHONE_MAX_W;
    if (phone === this.phone) return;
    this.phone = phone;
    this.build();
  }

  resetZoom(): void {
    // chartjs-plugin-zoom calls onZoomComplete → the link's zoom window is dropped
    this.chartDirectives?.forEach(d => (d.chart as any)?.resetZoom());
    this.zoomed = false;
  }

  /** After a drag / pinch zoom, a reset and a restored linked window (Chart.js calls it outside Angular). */
  private onZoomComplete(): void {
    this.zone.run(() => {
      this.zoomed = this.chartDirectives?.some(d => !!(d.chart as any)?.isZoomedOrPanned?.()) ?? false;
    });
  }

  /** Slot duration in hours, derived from the first two timestamps. */
  private slotHours(): number {
    if (this.data.length < 2) return 1;
    const diff = (this.data[1].ts - this.data[0].ts) / 3_600_000;
    return diff > 0 ? diff : 1;
  }

  /** Convert kWh (per slot) to average kW over that slot. */
  private toKw(kwh: number): number {
    const sh = this.slotHours();
    return kwh / sh;
  }

  private fmtKw(kw: number): string {
    if (Math.abs(kw) < 0.1) return (kw * 1000).toFixed(0) + ' W';
    return kw.toFixed(2) + ' kW';
  }

  /** "HH:mm" in Berlin time (the backend's day), in the UI locale. */
  private fmtTime(tsMs: number): string {
    return new Date(tsMs).toLocaleTimeString(uiLocale(), {
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin',
    });
  }

  ngOnChanges(_c: SimpleChanges): void {
    this.build();
  }

  private build(): void {
    const text  = this.darkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.75)';
    const grid  = this.darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)';
    const phone = this.phone;
    const tick  = phone ? 10 : 11;
    // new options drop the chart's zoom; the deepLink plugin restores a linked one (→ onZoomComplete)
    this.zoomed = false;

    // Linear time axis (epoch ms, labelled in Berlin time), so a zoom window is a time window.
    // Phones: ~6 ticks (every 4 h over a day) and no axis titles (the ticks carry the units).
    const xScale = {
      type: 'linear' as const,
      bounds: 'data' as const,
      afterBuildTicks: (axis: any) => { axis.ticks = berlinTimeTicks(axis.min, axis.max, phone ? 6 : 12); },
      ticks:  { color: text, maxRotation: 0, font: { size: tick }, callback: (val: string | number) => this.fmtTime(Number(val)) },
      grid:   { color: grid },
      title:  { display: !phone, text: this.translate.instant('HOURLY_AXIS_TIME'), color: text, font: { size: 11 } },
    };
    const yScale = {
      min: 0,
      // shown as kW with 2 decimals: a span of at least 10 × 0.01 kW (an almost idle day stays flat)
      resolution: 0.01,
      ticks: {
        color: text,
        callback: (val: string | number) => this.fmtKw(val as number),
        font: { size: tick },
        ...(phone ? { maxTicksLimit: 6 } : {}),
      },
      grid:  { color: grid },
      title: { display: !phone, text: this.translate.instant('HOURLY_AXIS_POWER'), color: text, font: { size: 11 } },
    };

    const tooltipTitle = (items: any[]) => items.length ? this.fmtTime(items[0].parsed.x) : '';
    const tooltipLabel = (item: any) =>
      ` ${item.dataset.label}: ${this.fmtKw(item.parsed.y ?? 0)}`;
    const tooltip = {
      callbacks: { title: tooltipTitle, label: tooltipLabel },
      ...(phone ? { padding: 6, boxWidth: 8, boxHeight: 8, titleFont: { size: 11 }, bodyFont: { size: 11 } } : {}),
    };
    const zoomOpts = dragZoom(() => this.onZoomComplete());
    // deepLink is not in Chart.js' plugin typings
    const deepLink = this.linkId ? { deepLink: { id: this.linkId } } as object : {};

    // ── Line chart (all datasets) ──────────────────────────────────────────
    this.lineData = {
      datasets: DATASETS.map(d => ({
        label:           this.translate.instant(d.label),
        linkKey:         d.field,
        data:            this.data.map(p => ({ x: p.ts, y: this.toKw(p[d.field] as number) })),
        borderColor:     d.color,
        backgroundColor: d.color,
        // points (and so the legend's line swatch) in the line's colour, not ng2-charts' default palette
        pointBackgroundColor: d.color,
        pointBorderColor:     d.color,
        borderWidth:     2,
        pointRadius:     this.data.length <= 12 ? 3 : 0,
        pointHoverRadius: 5,
        tension:         0.3,
        fill:            false,
      })),
    };
    this.lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: phone
            ? { color: text, boxWidth: 10, padding: 8, font: { size: 11 } }
            : { color: text, boxWidth: 12, padding: 12, font: { size: 12 } },
        },
        tooltip,
        zoom: zoomOpts,
        ...deepLink,
      },
      scales: { x: xScale, y: yScale },
    };

    // ── Bar chart (single dataset) ─────────────────────────────────────────
    const active = DATASETS.find(d => d.field === this.field);
    const slotMs = this.slotHours() * 3_600_000;
    const first = this.data[0]?.ts;
    const last  = this.data[this.data.length - 1]?.ts;
    this.barData = {
      datasets: active ? [{
        label:           this.translate.instant(active.label),
        linkKey:         active.field,
        data:            this.data.map(p => ({ x: p.ts, y: this.toKw(p[active.field] as number) })),
        backgroundColor: active.color,
        borderColor:     active.color,
        borderWidth:     0,
      } as ChartDataset<'bar', { x: number; y: number }[]>] : [],
    };
    this.barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip,
        zoom: zoomOpts,
        ...deepLink,
      },
      scales: {
        // each bar centred on its slot start, half a slot of room at both ends (no tick-based offset)
        x: {
          ...xScale, offset: false, grid: { color: grid, offset: false },
          ...(first != null && last != null ? { min: first - slotMs / 2, max: last + slotMs / 2 } : {}),
        },
        y: yScale,
      },
    };
  }
}
